import { useEffect, useRef, useState } from 'react';
import { Plus, Copy, ExternalLink, X, CheckCircle2, MessageCircleWarning, Upload, FileText, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import { Badge } from '../ui/Badge';
import { APROVACAO_PUBLICA_STATUS } from '../../lib/constants';

export function AprovacoesSection({ demanda, podeEditar }) {
  const [lista, setLista] = useState([]);
  const [adding, setAdding] = useState(false);

  const appOrigin = window.location.origin;

  const load = () => api.get(`/demandas/${demanda.codigo}/aprovacoes`).then((r) => setLista(r.aprovacoes));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [demanda.codigo]);

  const cancelar = async (id) => {
    if (!confirm('Cancelar este pedido de aprovação?')) return;
    await api.post(`/demandas/${demanda.codigo}/aprovacoes/${id}/cancelar`);
    load();
  };

  const copyLink = (token) => {
    const link = `${appOrigin}/aprovar/${token}`;
    navigator.clipboard.writeText(link);
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong>Aprovações públicas</strong>
          <div className="text-xs muted">Gere um link para o solicitante aprovar via WhatsApp sem precisar logar.</div>
        </div>
        {podeEditar && !adding && (
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
            <Plus size={14} /> Novo pedido
          </button>
        )}
      </div>
      <div className="card-body">
        {adding && (
          <AddForm
            demandaId={demanda.codigo}
            onCancel={() => setAdding(false)}
            onCreated={() => { setAdding(false); load(); }}
          />
        )}

        {lista.length === 0 ? (
          <div className="text-sm muted">Nenhum pedido de aprovação ainda.</div>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {lista.map((a) => {
              const s = APROVACAO_PUBLICA_STATUS[a.status];
              const link = `${appOrigin}/aprovar/${a.token}`;
              return (
                <div key={a.id} style={{ padding: 12, border: '1px solid var(--c-border)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        <Badge color={s.color}>{s.label}</Badge>
                        <strong style={{ fontSize: 14 }}>{a.titulo}</strong>
                      </div>
                      <div className="text-xs muted">
                        criado por {a.criado_por_nome} em {format(new Date(a.created_at), 'dd/MM/yyyy HH:mm')}
                        {a.expira_em && a.status === 'pendente' && ` · expira em ${format(new Date(a.expira_em), 'dd/MM/yyyy')}`}
                      </div>
                      {a.descricao && <div className="text-sm" style={{ marginTop: 6 }}>{a.descricao}</div>}
                      {a.arquivo_url && (
                        <a href={a.arquivo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', fontSize: 12, wordBreak: 'break-all', display: 'block', marginTop: 4 }}>
                          🔗 {a.arquivo_url}
                        </a>
                      )}
                      {a.decisao_comentario && (
                        <div style={{ marginTop: 8, padding: 8, background: a.status === 'aprovada' ? 'var(--c-success-soft)' : 'var(--c-warning-soft)', borderRadius: 6, fontSize: 13 }}>
                          {a.status === 'aprovada' ? <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <MessageCircleWarning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                          {a.decisao_comentario}
                          {a.decidido_em && <div className="text-xs muted" style={{ marginTop: 2 }}>em {format(new Date(a.decidido_em), 'dd/MM/yyyy HH:mm')}</div>}
                        </div>
                      )}
                    </div>
                    {podeEditar && a.status === 'pendente' && (
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => cancelar(a.id)} title="Cancelar">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {a.status === 'pendente' && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => copyLink(a.token)}>
                        <Copy size={13} /> Copiar link
                      </button>
                      <a className="btn btn-secondary btn-sm" href={link} target="_blank" rel="noreferrer">
                        <ExternalLink size={13} /> Abrir
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const MAX_MB = 20;
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,video/mp4,video/webm,video/quicktime';

function AddForm({ demandaId, onCancel, onCreated }) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [expiraDias, setExpiraDias] = useState(14);

  const [modo, setModo] = useState('upload'); // 'upload' | 'link'
  const [linkUrl, setLinkUrl] = useState('');
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null); // { url, filename, mime, size, originalName }
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const onPick = (e) => {
    setErr('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`Arquivo maior que ${MAX_MB}MB. Comprima antes de enviar.`);
      return;
    }
    setFile(f);
    setUploaded(null);
    setProgress(0);
  };

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    setErr('');
    try {
      const res = await api.upload('/uploads', file, (pct) => setProgress(pct));
      setUploaded(res);
    } catch (e) {
      setErr(e.message || 'Falha no upload');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!titulo.trim()) { setErr('Informe o título.'); return; }

    let finalUrl = null;
    if (modo === 'link') {
      finalUrl = linkUrl.trim() || null;
    } else if (modo === 'upload') {
      if (!uploaded) {
        if (file) {
          setErr('Clique em "Enviar arquivo" antes de gerar.');
          return;
        }
      } else {
        finalUrl = uploaded.url;
      }
    }

    setSaving(true);
    try {
      await api.post(`/demandas/${demandaId}/aprovacoes`, {
        titulo, descricao: descricao || null,
        arquivo_url: finalUrl,
        expira_em_dias: parseInt(expiraDias, 10) || 14,
      });
      onCreated?.();
    } catch (ex) {
      setErr(ex.message || 'Erro ao criar');
    } finally {
      setSaving(false);
    }
  };

  const isImage = uploaded?.mime?.startsWith('image/');
  const fmtSize = (n) => n < 1024 ? `${n}B` : n < 1024 * 1024 ? `${Math.round(n / 1024)}KB` : `${(n / (1024 * 1024)).toFixed(1)}MB`;

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 12, marginBottom: 14, padding: 14, background: 'var(--c-surface-2)', borderRadius: 10 }}>
      <input className="input" required placeholder="Título (ex: Arte do mutirão de saúde)" value={titulo} onChange={(e) => setTitulo(e.target.value)} />

      <div>
        <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Material</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <button type="button" onClick={() => setModo('upload')} className={`btn btn-sm ${modo === 'upload' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>
            <Upload size={13} /> Enviar arquivo
          </button>
          <button type="button" onClick={() => setModo('link')} className={`btn btn-sm ${modo === 'link' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>
            <ExternalLink size={13} /> Colar link
          </button>
        </div>

        {modo === 'upload' ? (
          <div>
            <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} style={{ display: 'none' }} />
            {!file && !uploaded && (
              <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-secondary btn-block" style={{ padding: 20, flexDirection: 'column', border: '2px dashed var(--c-border-strong)', background: 'white' }}>
                <Upload size={22} color="var(--c-primary)" />
                <span style={{ marginTop: 6, fontSize: 13 }}>Escolher arquivo</span>
                <span className="text-xs muted">PNG · JPG · PDF · MP4 (até {MAX_MB}MB)</span>
              </button>
            )}

            {file && !uploaded && (
              <div style={{ padding: 12, background: 'white', borderRadius: 10, border: '1px solid var(--c-border)' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  {file.type.startsWith('image/') ? <ImageIcon size={20} color="var(--c-primary)" /> : <FileText size={20} color="var(--c-primary)" />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }} className="truncate">{file.name}</div>
                    <div className="text-xs muted">{fmtSize(file.size)} · {file.type}</div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                    <X size={14} />
                  </button>
                </div>
                {uploading ? (
                  <div>
                    <div style={{ height: 6, background: 'var(--c-border)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${progress}%`, height: '100%', background: 'var(--c-primary)', transition: 'width 0.2s' }} />
                    </div>
                    <div className="text-xs muted" style={{ marginTop: 4 }}>Enviando... {progress}%</div>
                  </div>
                ) : (
                  <button type="button" className="btn btn-primary btn-block btn-sm" onClick={doUpload}>
                    <Upload size={14} /> Enviar arquivo
                  </button>
                )}
              </div>
            )}

            {uploaded && (
              <div style={{ padding: 12, background: 'var(--c-success-soft)', borderRadius: 10, border: '1px solid #86efac' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <CheckCircle2 size={18} color="var(--c-success)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d' }}>Upload concluído</div>
                    <div className="text-xs" style={{ color: '#166534' }}>{uploaded.originalName} · {fmtSize(uploaded.size)}</div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => { setUploaded(null); setFile(null); if (fileRef.current) fileRef.current.value = ''; }} title="Trocar arquivo">
                    <X size={14} />
                  </button>
                </div>
                {isImage && <img src={uploaded.url} alt="Preview" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 6, background: 'white' }} />}
              </div>
            )}
          </div>
        ) : (
          <input className="input" type="url" placeholder="https://drive.google.com/..." value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
        )}
      </div>

      <textarea className="textarea" placeholder="Observações para o aprovador (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={2} />

      <div className="row" style={{ gap: 10 }}>
        <label className="text-sm muted">Validade (dias):</label>
        <input className="input" type="number" min="1" max="90" style={{ maxWidth: 100 }} value={expiraDias} onChange={(e) => setExpiraDias(e.target.value)} />
      </div>

      {err && <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div>}

      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || uploading}>
          {saving ? 'Salvando...' : 'Gerar e notificar'}
        </button>
      </div>
    </form>
  );
}
